import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { HotelCardProvider } from 'components/HotelCardContext';
import { HotelCardDesktop } from 'components/HotelCardDesktop';
import { HotelDetailsTabs, TabId } from 'components/HotelDetailsTabs';
import { PriceWatchCTAWrapper } from 'components/PriceWatchCTAWrapper/PriceWatchCTAWrapper';
import { useHotelCardActions } from 'hooks/useHotelCardActions';
import { trackTimingEvent } from 'middleware/analytics';
import { getParametrizedHotelContext } from 'middleware/analytics/selectors';
import { setHotelDetailsOverlayState } from 'modules/overlay/actions';
import { getHotelDetailsOverlayInfo } from 'modules/overlay/selectors';
import { hideOverlay } from 'modules/overlay/slice';
import { getAnchorHotelId, getHotelHasMoreThanOneOffer } from 'modules/sapiSearch/selectors';
import { getHotelId, getListOfValues } from 'modules/search/selectors';
import { getClosestDataIdToEventTarget } from 'utils';
import { SelectedElement, selectedElementToTabIdMapping } from 'utils/selectedElement';
import { NoStyleButton } from '@daedalus/atlas/helpers/NoStyleButton';
import { Icon } from '@daedalus/atlas/Icon';
import { ANIMATION_TYPES, Overlay } from '@daedalus/atlas/Overlay';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { Action, AnalyticsContext, Category, Entity, Team } from '@daedalus/core/src/analytics/types/Events';
import { PriceWatchToggle } from '@daedalus/shared/src/priceWatch/components/PriceWatchToggle/PriceWatchToggle';
import { useGetHotelDetails } from './useGetHotelDetails';
const overlayStyles = "o1oknjrz";
const overlayContainerStyles = () => ({
  display: 'flex',
  'align-items': 'center',
  'justify-content': 'center'
});
const CardContentWrapper = /*#__PURE__*/styled('div')({
  name: "CardContentWrapper",
  class: "c1xkdu4l",
  propsAsIs: false
});
const HotelCardWrapper = /*#__PURE__*/styled('div')({
  name: "HotelCardWrapper",
  class: "h1njim8l",
  propsAsIs: false
});
const PriceWatchWrapper = /*#__PURE__*/styled('div')({
  name: "PriceWatchWrapper",
  class: "ph49g17",
  propsAsIs: false
});
const _exp8 = /*#__PURE__*/() => NoStyleButton;
const CloseButtonElement = /*#__PURE__*/styled(_exp8())({
  name: "CloseButtonElement",
  class: "c14asjga",
  propsAsIs: true
});
const HotelDetailsOverlayDesktop = ({
  isOpen
}: {
  isOpen: boolean;
}) => {
  const dispatch = useDispatch();
  const {
    hotelId,
    activeTab,
    initialImageIndex
  } = useSelector(getHotelDetailsOverlayInfo);
  const hotelIdFromUrl = useSelector(getHotelId);
  const anchorHotelIdFromState = useSelector(getAnchorHotelId);
  const anchorHotelId = anchorHotelIdFromState || hotelIdFromUrl;
  const hotel = useGetHotelDetails(hotelId);
  const {
    trackUserHotelCardClicked
  } = useHotelCardActions();
  const hotelHasMoreThanOneOffer = useSelector(state => getHotelHasMoreThanOneOffer(state, hotelId));
  const listOfValues = useSelector(getListOfValues);
  const handleTrackDetailsOverlayClose = useCallback((entity: Entity) => {
    const analyticsContext = {
      [AnalyticsContext.HotelContext]: getParametrizedHotelContext(hotelId),
      [AnalyticsContext.HotelInfoContext]: getParametrizedHotelContext(hotelId)
    };
    dispatch(trackEvent({
      category: Category.User,
      entity,
      action: Action.Clicked,
      analyticsContext
    }));
  }, [dispatch, hotelId]);
  const updateHotelDetailsOverlayState = useCallback((tabId: TabId, imageIndex: number) => {
    dispatch(setHotelDetailsOverlayState(hotelId, tabId, imageIndex));
  }, [hotelId, dispatch]);
  const handleHotelCardClick = useCallback(e => {
    const selectedElement = getClosestDataIdToEventTarget(e) || SelectedElement.BlankSpace;
    const selectedTabId = selectedElementToTabIdMapping[selectedElement] || TabId.Photos;
    trackUserHotelCardClicked({
      hotelId: hotelId,
      component: 'ListViewHotelCard',
      selectedElement
    });
    if (selectedTabId !== activeTab) {
      updateHotelDetailsOverlayState(selectedTabId, initialImageIndex);
    }
  }, [activeTab, initialImageIndex, updateHotelDetailsOverlayState]);
  const handleHotelDetailsClose = (entity: Entity) => {
    dispatch(hideOverlay());
    handleTrackDetailsOverlayClose(entity);
  };
  useEffect(() => {
    if (!hotel || !isOpen) return;
    trackTimingEvent(dispatch, 'Loaded', 'hotelCardClick-start', Entity.HotelDetailsOverlay, Team.Default, true);
  }, [hotel, isOpen]);
  if (!hotel) return null;
  return <Overlay shadeBackground customStyles={overlayStyles} containerStyles={overlayContainerStyles()} isOpen={isOpen} animationType={ANIMATION_TYPES.FADE_IN_OUT} onClose={() => handleHotelDetailsClose(Entity.DetailsOverlayShadow)} onShadeBackgroundClick={() => handleHotelDetailsClose(Entity.DetailsOverlayShadow)}>
      <CardContentWrapper>
        <CloseButtonElement onClick={() => handleHotelDetailsClose(Entity.DetailsOverlayCloseButton)}>
          <Icon name="Close" size="lg" />
        </CloseButtonElement>
        <HotelCardWrapper onClick={handleHotelCardClick}>
          <HotelCardProvider hotelId={hotelId} isAnchor={anchorHotelId === hotelId} isHotelDetailsOverlay>
            <HotelCardDesktop />
          </HotelCardProvider>
        </HotelCardWrapper>
        <PriceWatchWrapper>
          <PriceWatchCTAWrapper hotel={hotel} ctaComponent={PriceWatchToggle} componentName="PriceWatchToggle" holdOutElement={null} searchIsCompleted />
        </PriceWatchWrapper>
        <HotelDetailsTabs initialImageIndex={initialImageIndex} activeTab={activeTab} hotel={hotel} listOfValues={listOfValues} showDealsTab={hotelHasMoreThanOneOffer} />
      </CardContentWrapper>
    </Overlay>;
};
export default HotelDetailsOverlayDesktop;

require("./HotelDetailsOverlayDesktop.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./HotelDetailsOverlayDesktop.tsx");